import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from '@apollo/client';
import { createCustomLogger } from '~Hooks';
import { onError } from '@apollo/client/link/error';
import { persistCache } from 'apollo3-cache-persist';
import store from './store';

/* This type ensures that you can't accidentally merge false */
type OnlyTrue = true;
const ENABLE_CACHE: OnlyTrue = true;

const clientLogger = createCustomLogger('apollo-client');

const API_URL: string = process.env.FEDERATION_URL!;
window.__DEV__ = Boolean(process.env.DEV);

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors && window.__DEV__) {
    graphQLErrors.slice(0, 10).forEach(({ message, locations, path }) => {
      clientLogger.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations,
        )}, Path: ${path}, Operation`,
        operation,
      );
    });
  }

  if (networkError) {
    clientLogger.warn(`[Network error]: ${networkError}, Operation`, operation);
    const offline = store.getState().offline;
    if (!offline) {
      store.dispatch({
        type: 'SET_CONNECTION_STATUS',
        offline: true,
      });
    }
  }
});

/**
 * Add JWT baerer token to all request headers.
 */
const AuthLink = new ApolloLink((operation, forward) => {
  const token = store.getState().authToken;
  operation.setContext((context: any) => ({
    ...context,
    headers: {
      ...context.headers,
      authorization: `Bearer ${token}`,
    },
  }));

  return forward(operation);
});

/**
 * Add trace id to all query headers.
 * Used to connect error logs.
 * The id is 12 char NanoId, updated on every new touch interaction
 */
const TraceIDLink = new ApolloLink((operation, forward) => {
  const traceId = store.getState().traceId;
  operation.setContext((context: any) => ({
    ...context,
    headers: {
      ...context.headers,
      'x-baemingo-trace-id': traceId,
    },
  }));

  return forward(operation);
});

/**
 * Adds the name of the query/mutation to the URL for easier
 * debugging in the network log. Only active in local dev
 */
const addOpNameToUrl = (uri: RequestInfo | URL, options: any) => {
  const { operationName } = JSON.parse(options.body);
  return fetch(`${uri}graphql?q=${operationName}`, options);
};

const link = ApolloLink.from([
  errorLink,
  AuthLink,
  TraceIDLink,
  createHttpLink({
    uri: API_URL,
    fetch: __DEV__ ? addOpNameToUrl : undefined,
  }),
]);

const cache = new InMemoryCache({
  // @ts-ignore
  dataIdFromObject: (obj) => {
    // We need to define the typename specifically when we fetch an item.
    // This helps us avoid adding the typename in all our readFragments around the POS.
    // If the typename does not include "Item", then we just fetch the object from the ID.
    if (obj.__typename?.includes('Item')) {
      return obj.__typename + obj.id;
    }

    return obj.id;
  },
  typePolicies: {
    Venue: {
      fields: {
        inventory: {
          merge(existing, incoming) {
            // Better, but not quite correct.
            return { ...existing, ...incoming };
          },
        },
        eventReports: {
          merge(existing, incoming) {
            // Better, but not quite correct.
            return { ...existing, ...incoming };
          },
        },
        eventOrderStatus: {
          merge(existing, incoming) {
            // Better, but not quite correct.
            return { ...existing, ...incoming };
          },
        },
      },
    },
  },
});

if (__DEV__ && ENABLE_CACHE && !process.env.TESTING) {
  persistCache({
    cache,
    // @ts-ignore
    storage: window.localStorage,
  });
}

const apolloClient = new ApolloClient({
  link,
  name: 'Order Manager',
  version: store.getState().appVersion,
  cache,
  assumeImmutableResults: true,
});

export default apolloClient;
